/**
 * Variant Dropdown Component
 *
 * Accessible <select> fallback for products with many variants (> 6) or where not
 * every variant has an image. Single-select: choosing an option adds that variant's
 * product to the configuration; the placeholder deselects.
 *
 * @package
 */

import PropTypes from 'prop-types';
import { __, sprintf } from '@wordpress/i18n';
import { decodeHtmlEntities } from '../helpers/decodeHtmlEntities';
import { getPriceFromProduct, formatPrice } from '../helpers/priceHelpers';

const VariantDropdown = ( { variants, selectedVariant, onSelect, vatMode, label, disabled } ) => {
	if ( ! Array.isArray( variants ) || variants.length === 0 ) {
		return null;
	}

	const selectId = `configurator-variant-select-${ variants[ 0 ].id }`;

	const handleChange = e => {
		const { value } = e.target;
		if ( value === '' ) {
			// Placeholder chosen: clear the current selection (toggle it off).
			if ( selectedVariant ) {
				onSelect( selectedVariant );
			}
			return;
		}
		onSelect( Number( value ) );
	};

	const selected = variants.find( v => v.id === selectedVariant ) || null;

	return (
		<div className="configurator-variant-dropdown">
			<label className="configurator-variant-dropdown-label" htmlFor={ selectId }>
				{ label }
			</label>
			<select
				id={ selectId }
				className="configurator-variant-dropdown-select"
				value={ selectedVariant || '' }
				onChange={ handleChange }
				disabled={ disabled }
			>
				<option value="">{ label }</option>
				{ variants.map( variant => {
					const variantLabel = decodeHtmlEntities( variant.label || variant.title ) || __( 'Variant', 'at-vehicle-configurator' );
					const price = getPriceFromProduct( variant, vatMode );
					return (
						<option key={ variant.id } value={ variant.id }>
							{ price > 0
								? sprintf(
									/* translators: 1: variant name, 2: price */
									__( '%1$s — %2$s', 'at-vehicle-configurator' ),
									variantLabel,
									formatPrice( price )
								)
								: variantLabel }
						</option>
					);
				} ) }
			</select>
			{ selected && (
				<div className="configurator-variant-dropdown-selected">
					{ selected.image && (
						<img
							src={ selected.image }
							alt={ decodeHtmlEntities( selected.label || selected.title ) || '' }
							className="configurator-variant-dropdown-selected-image"
						/>
					) }
					<span className="configurator-variant-dropdown-selected-price">
						{ formatPrice( getPriceFromProduct( selected, vatMode ) ) }
					</span>
				</div>
			) }
		</div>
	);
};

VariantDropdown.propTypes = {
	variants: PropTypes.array,
	selectedVariant: PropTypes.number,
	onSelect: PropTypes.func.isRequired,
	vatMode: PropTypes.oneOf( [ 'including', 'excluding' ] ).isRequired,
	label: PropTypes.string,
	disabled: PropTypes.bool,
};

VariantDropdown.defaultProps = {
	variants: [],
	selectedVariant: null,
	label: __( 'Choose a variation', 'at-vehicle-configurator' ),
	disabled: false,
};

export default VariantDropdown;
